import { useMemo } from "react";
import useSWR from "swr";
import { swrFetcher } from "@/lib/api";
import { withLocale } from "@/lib/widget-locale";
import { useAuthStore } from "@/store/auth-store";

export type PackagePass = {
  id: string;
  title: string;
  description: string | null;
  price: number;
  vat: number | null;
  credits: number | null;
  durationDays: number | null;
  isPopular: boolean | null;
  isOneTimeUse?: boolean;
  // Classes dont les seances sont reservees automatiquement a l'achat. Plus
  // d'une : la personne choisit la sienne avant de payer.
  autoBookCourses?: AutoBookCourse[];
};

export type AutoBookCourse = {
  id: string;
  title: string;
  /** 0 = dimanche, heure de Zurich. */
  weekday: number;
  /** "15:45", heure de Zurich. */
  time: string;
};

/** « mardi 15:45 », dans la langue du widget. */
export function formatAutoBookSlot(course: AutoBookCourse, locale?: string) {
  // Le 7 janvier 2024 est un dimanche : on part de la pour nommer le jour.
  const day = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(2024, 0, 7 + course.weekday)));
  return `${day} ${course.time}`;
}

export type PackageSubscription = {
  id: string;
  title: string;
  description: string | null;
  price: number;
  vat: number | null;
  interval: string;
  intervalCount: number | null;
  creditsPerPeriod: number | null;
  isPopular: boolean | null;
};

export function useStudioPackages() {
  const { studioId, pricingIds } = useMemo(() => {
    if (typeof window === "undefined") {
      return { studioId: null, pricingIds: null };
    }
    const params = new URLSearchParams(window.location.search);
    return {
      studioId: params.get("studioId"),
      pricingIds: params.get("pricingIds"),
    };
  }, []);

  // `pricingIds` et la langue font partie de la cle SWR : deux widgets sur la
  // meme page demandent des tarifs -- ou une langue -- differents et ne doivent
  // pas partager une reponse.
  const key = studioId
    ? withLocale(
        `/api/widget/public/packages/${studioId}${
          pricingIds ? `?pricingIds=${encodeURIComponent(pricingIds)}` : ""
        }`,
      )
    : null;

  const { data, isLoading, error } = useSWR<{
    passes: PackagePass[];
    subscriptions: PackageSubscription[];
  }>(key, swrFetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60000,
  });

  // Une cliente connectee ne voit plus les passes a usage unique qu'elle a
  // deja achetes (le checkout les refuserait).
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: purchased } = useSWR<{ passIds: string[] }>(
    isAuthenticated ? "/api/widget/profile/passes/one-time-purchased" : null,
    swrFetcher,
    { revalidateOnFocus: false },
  );

  const passes = useMemo(() => {
    const all = data?.passes ?? [];
    const bought = new Set(purchased?.passIds ?? []);
    return all.filter((pass) => !(pass.isOneTimeUse && bought.has(pass.id)));
  }, [data?.passes, purchased?.passIds]);

  return {
    passes,
    subscriptions: data?.subscriptions ?? [],
    isLoading,
    error,
  };
}
